/**
 * Worker API client.
 *
 * Cookie-only session: the browser attaches the __Host-ballast_session cookie
 * automatically and JS never sees it (HttpOnly). We therefore only ever need
 * `credentials: 'same-origin'` — there is no token to store, which is the
 * whole point of the cookie-only design (§15).
 *
 * CSRF: the Worker requires a same-origin Sec-Fetch-Site / Origin on every
 * state-changing request, plus SameSite=Strict on the cookie. We additionally
 * send X-Requested-With so the Worker can reject any request that did not come
 * from fetch() (a cross-origin form post cannot set a custom header).
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = init.method ?? 'GET';
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (method !== 'GET' && method !== 'HEAD') {
    headers.set('X-Requested-With', 'ballast');
    if (init.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
  }

  const res = await fetch(path, { ...init, method, headers, credentials: 'same-origin' });

  if (res.status === 204) return undefined as T;

  const isJson = (res.headers.get('content-type') ?? '').includes('application/json');
  const payload = isJson ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    const err = (payload ?? {}) as { code?: string; message?: string };
    throw new ApiError(
      res.status,
      err.code ?? 'unknown',
      err.message ?? `Request failed (${res.status})`,
    );
  }
  return payload as T;
}

export interface HealthResponse {
  ok: boolean;
  version: string;
  time: string;
}

export interface SessionUser {
  userId: string;
  email: string;
  /** When the Getting started card was finished or hidden (v10.0.0). NULL = show it. */
  onboardedAt?: string | null;
}

/** Every status the server sends (src/ledger-source/types.ts ConnectionStatus). */
export type ConnectionStatus = 'ok' | 'reauth_required' | 'pending_disconnect' | 'revoked_by_user';

/** Whether the provider has finished sending this connection's history (v7.1.0). */
export type HistoryStatus = 'unknown' | 'not_ready' | 'initial_complete' | 'historical_complete';

export interface ConnectionSummary {
  itemId: string;
  institutionName: string | null;
  status: ConnectionStatus;
  lastSyncedAt: string | null;
  accountCount: number;
  historyStatus: HistoryStatus;
}

export interface MeResponse {
  user: SessionUser;
  entities: Array<{
    id: string;
    name: string;
    state: string;
    /** Parts per million (250000 = 25%). NULL = not set, never a default. */
    taxRatePpm: number | null;
    getRatePpm: number | null;
  }>;
  connections: ConnectionSummary[];
}

export const api = {
  health: () => request<HealthResponse>('/api/health'),
  me: () => request<MeResponse>('/api/me'),
  login: (email: string, password: string) =>
    request<{ user: SessionUser }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),
  /**
   * Resolves with how many OTHER devices were signed out. The session making the
   * request stays valid. A wrong current password is a 403 with code
   * `password_change_refused`, never a 401 — a 401 here would read as "your
   * session ended" and bounce the operator to the sign-in screen mid-form.
   */
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ changed: true; otherSessionsRevoked: number }>('/api/auth/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
};

/* -------------------------------------------------------------------------
 * First run (v10.0.0)
 * ---------------------------------------------------------------------- */

export const onboardingApi = {
  /** Add a business: the entity and its Unallocated envelope. No money moves. */
  addBusiness: (name: string, state: string) =>
    request<{ entity: { id: string; name: string; state: string } }>('/api/entities', {
      method: 'POST',
      body: JSON.stringify({ name, state }),
    }),
  /** Hide the Getting started card for good. */
  done: () => request<void>('/api/onboarding/done', { method: 'POST' }),
};

/* -------------------------------------------------------------------------
 * Push notifications (v10.0.0)
 * ---------------------------------------------------------------------- */

export interface PushPrefs {
  needsYou: boolean;
  reconnect: boolean;
  bills: boolean;
}

export interface PushInfo {
  /** False when the server cannot send (no field key). */
  available: boolean;
  /** The applicationServerKey (base64url P-256 point). */
  publicKey: string | null;
  prefs: PushPrefs | null;
}

export const pushApi = {
  info: () => request<PushInfo>('/api/push'),
  save: (subscription: unknown) =>
    request<void>('/api/push/subscription', {
      method: 'PUT',
      body: JSON.stringify(subscription),
    }),
  remove: (endpoint: string) =>
    request<void>('/api/push/subscription', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    }),
  setPrefs: (prefs: PushPrefs) =>
    request<{ prefs: PushPrefs }>('/api/push/prefs', {
      method: 'PUT',
      body: JSON.stringify(prefs),
    }),
  test: () =>
    request<{ sent: number; gone: number; failed: number }>('/api/push/test', { method: 'POST' }),
};

/* -------------------------------------------------------------------------
 * Envelopes (Slice 1)
 * ---------------------------------------------------------------------- */

export interface ApiEnvelope {
  id: string;
  name: string;
  type: 'unallocated' | 'buffer' | 'tax' | 'spend' | 'save';
  /** NULL when the bank has not reported an available balance. */
  balanceMinor: number | null;
  targetMinor: number | null;
  targetDate: string | null;
  zone: string | null;
  /**
   * Waterfall order: HIGHER fills first. Every envelope starts at 0, which makes
   * screen order the order — the fill-order sheet writes 1..n over that.
   */
  priority: number;
}

export interface EnvelopesResponse {
  entityId: string;
  envelopes: ApiEnvelope[];
  /** NULL means "we don't know", never a confident zero. */
  safeToSpendMinor: number | null;
  /** Positive when allocations exceed the cash actually available. */
  overAllocatedMinor: number;
  invariant: 'balanced' | 'cash_ahead' | 'envelopes_ahead' | 'indeterminate';
  /** NULL until there is income history to measure a monthly draw from. */
  runwayMonths: number | null;
  /** The draw runway is measured against; NULL under three complete months. */
  monthlyDraw: { drawMinor: number; basisMonths: number; seasonal: boolean } | null;
}

export const envelopeApi = {
  list: (entityId: string) =>
    request<EnvelopesResponse>(`/api/entities/${encodeURIComponent(entityId)}/envelopes`),

  create: (
    entityId: string,
    body: {
      name: string;
      type: ApiEnvelope['type'];
      targetMinor?: number | null;
      /** 'YYYY-MM-DD'. The Worker refuses anything else rather than storing it. */
      targetDate?: string | null;
      /** Canvas grouping (v8.0.0): 'reserved' or 'purpose'. NULL = by type. */
      zone?: string | null;
    },
  ) =>
    request<{ id: string }>(`/api/entities/${encodeURIComponent(entityId)}/envelopes`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Tap-to-fund. `idempotencyKey` is generated per ATTEMPT, not per retry, so
   * a dropped response that the client retries cannot allocate twice.
   */
  transfer: (
    entityId: string,
    body: {
      fromEnvelopeId: string;
      toEnvelopeId: string;
      amountMinor: number;
      memo?: string;
      idempotencyKey?: string;
    },
  ) =>
    request<{ entryId?: string; duplicate?: boolean }>(
      `/api/entities/${encodeURIComponent(entityId)}/transfers`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  setRates: (entityId: string, body: { taxRatePpm: number | null; getRatePpm: number | null }) =>
    request<{ taxRatePpm: number | null; getRatePpm: number | null }>(
      `/api/entities/${encodeURIComponent(entityId)}/rates`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  complete: (entityId: string, envelopeId: string) =>
    request<{ sweptMinor: number }>(
      `/api/entities/${encodeURIComponent(entityId)}/envelopes/${encodeURIComponent(envelopeId)}/complete`,
      { method: 'POST' },
    ),

  /**
   * Which envelope the waterfall fills first. `order` is first-filled first.
   * Moves no money and stages nothing.
   */
  setOrder: (entityId: string, order: string[]) =>
    request<{ order: string[] }>(`/api/entities/${encodeURIComponent(entityId)}/envelope-order`, {
      method: 'PUT',
      body: JSON.stringify({ order }),
    }),
};

/* -------------------------------------------------------------------------
 * Deposits — what each one IS (v7.0.0, Blueprint §6)
 * ---------------------------------------------------------------------- */

export type ApiInflowType = 'income' | 'transfer' | 'financing' | 'refund' | 'injection';

export interface ApiDeposit {
  txnKey: string;
  /** yyyy-mm-dd */
  date: string;
  amountMinor: number;
  description: string;
  counterparty: string | null;
  /** The bank's own label. Advisory: nothing acts on it. */
  bankHint: string | null;
  /** NULL = UNKNOWN — "needs a look". Never income by default. */
  inflowType: ApiInflowType | null;
  /** Who decided: the operator on this deposit, or a sender they taught. */
  inflowSource: 'operator' | 'counterparty' | null;
  skim: { status: 'pending' | 'approved' | 'dismissed' | 'expired'; amountMinor: number } | null;
}

export interface DepositsResponse {
  entityId: string;
  /** How far back a deposit is worth asking about (days). */
  windowDays: number;
  deposits: ApiDeposit[];
}

export interface ApiClassifyResult {
  txnKey: string;
  inflowType: ApiInflowType;
  /** The sender the answer was taught to. NULL = nameless deposit, nothing learned. */
  learnedFrom: string | null;
  appliedToOthers: number;
  skim: ApiDeposit['skim'];
  withdrewSkim: boolean;
  approvedSkimStays: boolean;
}

export const depositApi = {
  list: (entityId: string) =>
    request<DepositsResponse>(`/api/entities/${encodeURIComponent(entityId)}/deposits`),

  classify: (entityId: string, txnKey: string, inflowType: ApiInflowType) =>
    request<ApiClassifyResult>(
      `/api/entities/${encodeURIComponent(entityId)}/deposits/${encodeURIComponent(txnKey)}/classify`,
      { method: 'POST', body: JSON.stringify({ inflowType }) },
    ),
};

/* -------------------------------------------------------------------------
 * Bills — the bank's recurring bills, as questions (v8.0.0, §9)
 * ---------------------------------------------------------------------- */

/** "Track this as a bill?" — nothing is tracked until the operator says so. */
export interface ApiBill {
  id: string;
  name: string;
  /** The bank's own text, shown small when it differs from the name. */
  description: string;
  cadence: 'weekly' | 'biweekly' | 'semi_monthly' | 'monthly' | 'annually' | 'unknown' | string;
  /** Fewer than three payments seen, or the bank won't say. */
  provisional: boolean;
  /** NULL = the bank did not say what it costs. Never shown as $0. */
  amountMinor: number | null;
  lastDate: string;
  /** NULL = no honest next date (an irregular bill with no prediction). */
  nextDueDate: string | null;
  accountName: string;
  accountMask: string | null;
}

/** A tracked bill the bank has seen paid: clear the cycle and roll forward. */
export interface ApiPaidBill {
  id: string;
  envelopeId: string;
  envelopeName: string;
  paidOn: string;
  paidAmountMinor: number | null;
  /** What clearing moves out of the envelope back to free cash. */
  clearMinor: number;
  nextDueDate: string | null;
  nextTargetMinor: number | null;
}

export interface BillsResponse {
  entityId: string;
  suggestions: ApiBill[];
  paid: ApiPaidBill[];
}

export const billApi = {
  list: (entityId: string) =>
    request<BillsResponse>(`/api/entities/${encodeURIComponent(entityId)}/bills`),

  track: (entityId: string, billId: string, body: { amountMinor?: number; name?: string } = {}) =>
    request<{ envelopeId: string; name: string; targetMinor: number; targetDate: string | null }>(
      `/api/entities/${encodeURIComponent(entityId)}/bills/${encodeURIComponent(billId)}/track`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  dismiss: (entityId: string, billId: string) =>
    request<{ ok: true }>(
      `/api/entities/${encodeURIComponent(entityId)}/bills/${encodeURIComponent(billId)}/dismiss`,
      { method: 'POST', body: '{}' },
    ),

  paid: (entityId: string, billId: string) =>
    request<{ clearedMinor: number; nextDueDate: string | null }>(
      `/api/entities/${encodeURIComponent(entityId)}/bills/${encodeURIComponent(billId)}/paid`,
      { method: 'POST', body: '{}' },
    ),
};

/* -------------------------------------------------------------------------
 * Proposals — the Needs You queue (v3)
 * ---------------------------------------------------------------------- */

export type ProposalKind =
  | 'income_allocation'
  | 'salary_draw'
  | 'buffer_action'
  | 'unassigned_spend'
  | 'tax_skim'
  | 'waterfall';

export interface ApiProposal {
  id: string;
  kind: ProposalKind;
  amountMinor: number;
  memo: string | null;
  createdAt: number;
  expiresAt: number | null;
  from: { id: string; name: string | null; type: string | null };
  to: { id: string; name: string | null; type: string | null };
  /** NULL when the bank has not reported the source balance. */
  sourceBalanceMinor: number | null;
  /**
   * Advisory. The server re-checks the live balance inside the statement that
   * commits, so this decides nothing — it exists so the screen can say "this
   * no longer fits" before the operator taps, rather than after.
   *
   * NULL means unknown, and unknown is neither true nor false.
   */
  affordableNow: boolean | null;
}

export interface ProposalsResponse {
  entityId: string;
  proposals: ApiProposal[];
}

export const proposalApi = {
  list: (entityId: string) =>
    request<ProposalsResponse>(`/api/entities/${encodeURIComponent(entityId)}/proposals`),

  approve: (entityId: string, proposalId: string) =>
    request<{ entryId: string; amountMinor: number }>(
      `/api/entities/${encodeURIComponent(entityId)}/proposals/${encodeURIComponent(proposalId)}/approve`,
      { method: 'POST' },
    ),

  /**
   * Change the amount before approving. Moves no money — the proposal is still
   * a suggestion afterwards, just a different one.
   */
  edit: (entityId: string, proposalId: string, amountMinor: number) =>
    request<{ amountMinor: number }>(
      `/api/entities/${encodeURIComponent(entityId)}/proposals/${encodeURIComponent(proposalId)}`,
      { method: 'PATCH', body: JSON.stringify({ amountMinor }) },
    ),

  /**
   * Stage the allocation waterfall as proposals. Moves no money; replaces any
   * waterfall suggestions still pending.
   */
  planWaterfall: (entityId: string) =>
    request<{ staged: number; totalMinor: number; remainingMinor: number; superseded: number }>(
      `/api/entities/${encodeURIComponent(entityId)}/waterfall`,
      { method: 'POST' },
    ),

  dismiss: (entityId: string, proposalId: string) =>
    request<{ dismissed: boolean }>(
      `/api/entities/${encodeURIComponent(entityId)}/proposals/${encodeURIComponent(proposalId)}/dismiss`,
      { method: 'POST' },
    ),
};

/* -------------------------------------------------------------------------
 * Banks — connecting one, and assigning its accounts to entities (Session 3)
 * ---------------------------------------------------------------------- */

export interface ApiAccount {
  id: string;
  name: string;
  mask: string | null;
  subtype: string;
  institutionName: string | null;
  /** NULL = the bank has not reported it. Never render as $0. */
  availableMinor: number | null;
  /** NULL = not assigned to any entity: counted nowhere. */
  entityId: string | null;
  budgetable: boolean;
}

export const bankApi = {
  linkToken: () =>
    request<{ linkToken: string; expiresAt: string; mode: 'plaid' | 'fake' }>(
      '/api/plaid/link-token',
      { method: 'POST' },
    ),
  exchange: (publicToken: string) =>
    request<{ itemId: string; institutionName: string | null; accounts: number }>(
      '/api/plaid/exchange',
      { method: 'POST', body: JSON.stringify({ publicToken }) },
    ),
  accounts: () => request<{ accounts: ApiAccount[] }>('/api/accounts'),
  assign: (accountId: string, body: { entityId: string | null; budgetable: boolean }) =>
    request<{ id: string }>(`/api/accounts/${encodeURIComponent(accountId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
};

/* -------------------------------------------------------------------------
 * Connections after linking (v7.1.0) — bank-side sync, reconnect, disconnect
 * ---------------------------------------------------------------------- */

export type BankSyncOutcome = 'synced' | 'cooling_down' | 'reconnect_needed' | 'failed';

/** One bank's answer to "Sync now". Each bank reports separately. */
export interface BankSyncResult {
  itemId: string;
  institutionName: string | null;
  outcome: BankSyncOutcome;
  /** ISO time of the last SUCCESSFUL sync. */
  lastSyncedAt: string | null;
  /** Seconds until this bank may be asked again, when cooling down. */
  retryAfterSeconds?: number;
}

/** What disconnecting does to one entity's free money. NULL = not known. */
export interface DisconnectImpact {
  entityId: string;
  name: string;
  freeBeforeMinor: number | null;
  freeAfterMinor: number | null;
  /** > 0 when envelopes will exceed the cash left; 0 when not; NULL unknown. */
  overAllocatedAfterMinor: number | null;
  noAccountsLeft: boolean;
}

export interface DisconnectPreview {
  itemId: string;
  institutionName: string | null;
  accounts: number;
  transactions: number;
  /** v9.0.0: receipts attached to those transactions — they go too. */
  receipts: number;
  /** Only the entities whose figures change. Empty = nothing depends on it. */
  entities: DisconnectImpact[];
}

const connectionPath = (itemId: string) => `/api/connections/${encodeURIComponent(itemId)}`;

export const connectionApi = {
  /** Ask every connected bank for anything new (server side, per-bank cooldown). */
  syncBanks: () => request<{ banks: BankSyncResult[] }>('/api/sync', { method: 'POST' }),
  /** Link token for Plaid's update mode. The access token never reaches the browser. */
  reconnectToken: (itemId: string) =>
    request<{ linkToken: string; expiresAt: string; mode: 'plaid' | 'fake' }>(
      `${connectionPath(itemId)}/reconnect`,
      { method: 'POST' },
    ),
  reconnected: (itemId: string) =>
    request<{ itemId: string; status: ConnectionStatus; lastSyncedAt: string | null }>(
      `${connectionPath(itemId)}/reconnected`,
      { method: 'POST' },
    ),
  disconnectPreview: (itemId: string) =>
    request<DisconnectPreview>(`${connectionPath(itemId)}/disconnect`),
  disconnect: (itemId: string) =>
    request<{ itemId: string; removed: { accounts: number; transactions: number } }>(
      connectionPath(itemId),
      { method: 'DELETE' },
    ),
};

/* -------------------------------------------------------------------------
 * Receipts (v9.0.0) — a photo or PDF on a transaction (docs/SECURITY.md §12)
 * ---------------------------------------------------------------------- */

/** What a receipt's bytes were found to be, on the server. Never the upload's claim. */
export type ApiReceiptType =
  'image/jpeg' | 'image/png' | 'image/webp' | 'image/heic' | 'application/pdf';

export interface ApiReceipt {
  id: string;
  txnKey: string;
  contentType: ApiReceiptType;
  sizeBytes: number;
  /** Epoch seconds. */
  createdAt: number;
}

export interface ApiTransaction {
  txnKey: string;
  date: string;
  /** Signed: positive is money in. */
  amountMinor: number;
  name: string;
  description: string;
  pending: boolean;
  /** Reversed by the bank. Listed only while it still carries a receipt. */
  removed: boolean;
  account: string;
  receipts: ApiReceipt[];
}

export interface TransactionsResponse {
  entityId: string;
  /** False until the owner sets the receipts key; then there is no upload to offer. */
  receiptsEnabled: boolean;
  limits: { maxBytes: number; perTransaction: number };
  transactions: ApiTransaction[];
  next: { beforeDate: string; beforeKey: string } | null;
}

export const receiptApi = {
  transactions: (entityId: string, next?: TransactionsResponse['next']) =>
    request<TransactionsResponse>(
      `/api/entities/${encodeURIComponent(entityId)}/transactions` +
        (next
          ? `?beforeDate=${encodeURIComponent(next.beforeDate)}&beforeKey=${encodeURIComponent(next.beforeKey)}`
          : ''),
    ),

  /** The raw bytes as the body. No filename is sent: the server never wants one. */
  upload: (entityId: string, txnKey: string, body: Blob) =>
    request<{ receipt: ApiReceipt }>(
      `/api/entities/${encodeURIComponent(entityId)}/transactions/${encodeURIComponent(txnKey)}/receipts`,
      { method: 'POST', body, headers: { 'Content-Type': 'application/octet-stream' } },
    ),

  /**
   * The bytes, as a Blob typed from the LIST's content type (the server sends
   * octet-stream on purpose). This is the only request that can read a receipt:
   * the server refuses anything without the app's header, so a link or an <img>
   * pointed at the same URL gets a 403.
   */
  file: async (receipt: ApiReceipt): Promise<Blob> => {
    const res = await fetch(`/api/receipts/${encodeURIComponent(receipt.id)}/file`, {
      headers: { 'X-Requested-With': 'ballast' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!res.ok) {
      const err = ((await res.json().catch(() => null)) ?? {}) as {
        code?: string;
        message?: string;
      };
      throw new ApiError(
        res.status,
        err.code ?? 'unknown',
        err.message ?? `Request failed (${res.status})`,
      );
    }
    return new Blob([await res.arrayBuffer()], { type: receipt.contentType });
  },

  remove: (receiptId: string) =>
    request<{ deleted: true; objectRemoved: boolean }>(
      `/api/receipts/${encodeURIComponent(receiptId)}`,
      { method: 'DELETE' },
    ),
};

/* -------------------------------------------------------------------------
 * Passkeys (v11.0.0) — src/routes/passkey.ts
 * ---------------------------------------------------------------------- */

export interface PasskeySummary {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
  backedUp: boolean;
}

export interface PasskeyList {
  /** Whether a passkey can be used on THIS host (false on workers.dev). */
  available: boolean;
  /** The host passkeys belong to, so the screen can name it. Null = off. */
  rpId: string | null;
  max: number;
  passkeys: PasskeySummary[];
}

export const passkeyApi = {
  /** Signed out: may the sign-in screen offer a passkey here? */
  status: () => request<{ available: boolean }>('/api/auth/passkey'),
  signInOptions: () =>
    request<import('./passkey-client').RequestOptionsJSON>('/api/auth/passkey/options', {
      method: 'POST',
    }),
  /** A refusal is a 401 with one message whatever the reason. */
  signIn: (response: unknown) =>
    request<{ user: SessionUser }>('/api/auth/passkey/login', {
      method: 'POST',
      body: JSON.stringify({ response }),
    }),
  list: () => request<PasskeyList>('/api/auth/passkeys'),
  /** Needs the current password; a wrong one is a 403, never a 401. */
  enrolOptions: (currentPassword: string) =>
    request<import('./passkey-client').CreationOptionsJSON>('/api/auth/passkeys/options', {
      method: 'POST',
      body: JSON.stringify({ currentPassword }),
    }),
  add: (name: string, response: unknown) =>
    request<{ added: true; id: string; name: string }>('/api/auth/passkeys', {
      method: 'POST',
      body: JSON.stringify({ name, response }),
    }),
  remove: (id: string, currentPassword: string) =>
    request<{ removed: true }>(`/api/auth/passkeys/${encodeURIComponent(id)}/remove`, {
      method: 'POST',
      body: JSON.stringify({ currentPassword }),
    }),
};
